import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Package, ExternalLink, Clock, FileText, Check,
  ChevronLeft, ChevronRight, DollarSign, MessageSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ServiceCapabilityBadges from './ServiceCapabilityBadges';
import { ServiceOffering, OFFERING_TYPES_BY_CATEGORY, OFFERING_LEAD_TIMES } from '@/types/serviceOffering';
import { ServiceCategory } from '@/types/serviceWizard';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface ServiceOfferingGridProps {
  offerings: ServiceOffering[];
  category: ServiceCategory;
  formatPrice?: (price?: number, currency?: string) => string | null;
  onContactForOffering?: (offering: ServiceOffering) => void;
}

const ServiceOfferingGrid: React.FC<ServiceOfferingGridProps> = ({
  offerings,
  category,
  formatPrice = (price, currency) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(price);
  },
  onContactForOffering
}) => {
  if (!offerings || offerings.length === 0) return null;

  // Get offering type label
  const getOfferingTypeLabel = (type: string) => {
    const types = OFFERING_TYPES_BY_CATEGORY[category] || [];
    const found = types.find(t => t.value === type);
    return found?.label || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get lead time label
  const getLeadTimeLabel = (leadTime: string) => {
    const found = OFFERING_LEAD_TIMES.find(lt => lt.value === leadTime);
    return found?.label || leadTime.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Category color themes
  const categoryThemes: Record<ServiceCategory, { border: string; bg: string; accent: string }> = {
    mro: { border: 'border-blue-200/60 dark:border-blue-700/40', bg: 'from-blue-50/50 dark:from-blue-950/30 to-indigo-50/30 dark:to-indigo-950/20', accent: 'text-blue-600 dark:text-blue-400' },
    camo: { border: 'border-violet-200/60 dark:border-violet-700/40', bg: 'from-violet-50/50 dark:from-violet-950/30 to-purple-50/30 dark:to-purple-950/20', accent: 'text-violet-600 dark:text-violet-400' },
    part21: { border: 'border-orange-200/60 dark:border-orange-700/40', bg: 'from-orange-50/50 dark:from-orange-950/30 to-amber-50/30 dark:to-amber-950/20', accent: 'text-orange-600 dark:text-orange-400' },
    training: { border: 'border-emerald-200/60 dark:border-emerald-700/40', bg: 'from-emerald-50/50 dark:from-emerald-950/30 to-green-50/30 dark:to-green-950/20', accent: 'text-emerald-600 dark:text-emerald-400' },
    operator: { border: 'border-sky-200/60 dark:border-sky-700/40', bg: 'from-sky-50/50 dark:from-sky-950/30 to-blue-50/30 dark:to-blue-950/20', accent: 'text-sky-600 dark:text-sky-400' },
    engineering: { border: 'border-slate-200/60 dark:border-slate-700/40', bg: 'from-slate-50/50 dark:from-slate-950/30 to-gray-50/30 dark:to-gray-950/20', accent: 'text-slate-600 dark:text-slate-400' },
    ground_ops: { border: 'border-amber-200/60 dark:border-amber-700/40', bg: 'from-amber-50/50 dark:from-amber-950/30 to-yellow-50/30 dark:to-yellow-950/20', accent: 'text-amber-600 dark:text-amber-400' },
    fbo_heliport: { border: 'border-teal-200/60 dark:border-teal-700/40', bg: 'from-teal-50/50 dark:from-teal-950/30 to-cyan-50/30 dark:to-cyan-950/20', accent: 'text-teal-600 dark:text-teal-400' },
    management: { border: 'border-rose-200/60 dark:border-rose-700/40', bg: 'from-rose-50/50 dark:from-rose-950/30 to-pink-50/30 dark:to-pink-950/20', accent: 'text-rose-600 dark:text-rose-400' },
    uas: { border: 'border-cyan-200/60 dark:border-cyan-700/40', bg: 'from-cyan-50/50 dark:from-cyan-950/30 to-blue-50/30 dark:to-blue-950/20', accent: 'text-cyan-600 dark:text-cyan-400' }
  };

  const theme = categoryThemes[category] || categoryThemes.mro;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {offerings.map((offering) => (
        <ServiceOfferingCard
          key={offering.id}
          offering={offering}
          category={category}
          theme={theme}
          getOfferingTypeLabel={getOfferingTypeLabel}
          getLeadTimeLabel={getLeadTimeLabel}
          formatPrice={formatPrice}
          onContact={onContactForOffering}
        />
      ))}
    </div>
  );
};

interface ServiceOfferingCardProps {
  offering: ServiceOffering;
  category: ServiceCategory;
  theme: { border: string; bg: string; accent: string };
  getOfferingTypeLabel: (type: string) => string;
  getLeadTimeLabel: (leadTime: string) => string;
  formatPrice: (price?: number, currency?: string) => string | null;
  onContact?: (offering: ServiceOffering) => void;
}

const ServiceOfferingCard: React.FC<ServiceOfferingCardProps> = ({
  offering,
  category,
  theme,
  getOfferingTypeLabel,
  getLeadTimeLabel,
  formatPrice,
  onContact
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const photos = offering.photos?.filter(Boolean) || [];
  const hasPhotos = photos.length > 0;
  const hasMultiplePhotos = photos.length > 1;

  const nextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Render price display based on price_type
  const renderPriceDisplay = () => {
    switch (offering.price_type) {
      case 'fixed':
        return (
          <div className="text-right flex-shrink-0">
            <p className={cn("text-xl font-bold", theme.accent)}>
              {formatPrice(offering.price, 'USD')}
            </p>
            {offering.lead_time && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 justify-end">
                <Clock className="w-3 h-3" />
                {getLeadTimeLabel(offering.lead_time)}
              </div>
            )}
          </div>
        );
      case 'starting_at':
        return (
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-muted-foreground">Starting at</p>
            <p className={cn("text-xl font-bold", theme.accent)}>
              {formatPrice(offering.price, 'USD')}
            </p>
          </div>
        );
      case 'quote':
        return (
          <Badge variant="secondary" className="h-fit">
            <MessageSquare className="w-3 h-3 mr-1" />
            Request Quote
          </Badge>
        );
      case 'contact':
        return (
          <Badge variant="outline" className="h-fit">
            Contact for Pricing
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Card className={cn(
      "overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300",
      theme.border,
      `bg-gradient-to-br ${theme.bg} dark:from-slate-900 dark:to-slate-950`
    )}>
      <div className="flex flex-col sm:flex-row">
        {/* Photo Gallery Section */}
        <div className={cn(
          "relative bg-gradient-to-br from-muted to-muted/50",
          hasPhotos ? "w-full sm:w-40 h-40 sm:h-auto" : "w-full sm:w-20 h-20 sm:h-auto"
        )}>
          {hasPhotos ? (
            <>
              <img
                src={photos[currentPhotoIndex]}
                alt={offering.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              {hasMultiplePhotos && (
                <>
                  <button
                    onClick={prevPhoto}
                    aria-label="Previous photo"
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={nextPhoto}
                    aria-label="Next photo"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  {/* Photo indicators */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {photos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPhotoIndex(idx);
                        }}
                        aria-label={`Go to photo ${idx + 1}`}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          idx === currentPhotoIndex
                            ? "bg-white scale-110"
                            : "bg-white/50 hover:bg-white/75"
                        )}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[80px]">
              <Package className={cn("w-8 h-8 opacity-40", theme.accent)} />
            </div>
          )}
        </div>

        {/* Content Section */}
        <CardContent className="flex-1 p-4 space-y-3">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-base text-foreground line-clamp-2">
                {offering.name}
              </h4>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge className={cn(
                  "border-0",
                  theme.accent.replace('text-', 'bg-').replace('-600', '-100'),
                  theme.accent
                )}>
                  {getOfferingTypeLabel(offering.offering_type)}
                </Badge>
              </div>
            </div>

            {/* Price Display */}
            {renderPriceDisplay()}
          </div>

          {/* Description */}
          {offering.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {offering.description}
            </p>
          )}

          {/* Helicopter Compatibility */}
          {offering.compatible_helicopters && offering.compatible_helicopters.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <HelicopterSilhouetteIcon className="w-3.5 h-3.5" />
                <span className="font-medium">Compatible Aircraft</span>
              </div>
              <ServiceCapabilityBadges
                helicopterCapabilityIds={offering.compatible_helicopters}
                engineCapabilityIds={[]}
                variant="compact"
                maxShow={4}
              />
            </div>
          )}

          {/* Category-specific fields */}
          {offering.category_data && (
            <div className="flex flex-wrap gap-2 text-xs">
              {/* Training-specific */}
              {offering.category_data.duration_value && offering.category_data.duration_unit && (
                <Badge variant="outline" className="gap-1">
                  <Clock className="w-3 h-3" />
                  {offering.category_data.duration_value} {offering.category_data.duration_unit}
                </Badge>
              )}
              {offering.category_data.certification_issued && (
                <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  <Check className="w-3 h-3" />
                  {offering.category_data.certification_issued}
                </Badge>
              )}
              {/* Part-21 specific */}
              {offering.category_data.part_number && (
                <span className="font-mono text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                  P/N: {offering.category_data.part_number}
                </span>
              )}
            </div>
          )}

          {/* Documentation Link */}
          {offering.documentation_url && (
            <Button
              variant="ghost"
              size="sm"
              className={cn("h-7 text-xs", theme.accent, "hover:bg-muted/50")}
              onClick={() => window.open(offering.documentation_url!, '_blank')}
            >
              <FileText className="w-3.5 h-3.5 mr-1" />
              Documentation
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}

          {/* Contact CTA for quote/contact types */}
          {(offering.price_type === 'quote' || offering.price_type === 'contact') && onContact && (
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => onContact(offering)}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Request Quote
            </Button>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

export default ServiceOfferingGrid;
